import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import LocalShipping from "@mui/icons-material/LocalShipping";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  GetWasteShipmentQuery,
  Issue,
  IssueLevel,
  ShippableContainerItemFragment,
  UnitType,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  formatQuantity,
  isLocalDateInFuture,
  NO_VALUE_PLACEHOLDER,
  UNIT_LABELS,
} from "../Containers/containerDisplay";
import {
  CANCEL_WASTE_SHIPMENT_MUTATION,
  DELETE_WASTE_SHIPMENT_MUTATION,
  GET_WASTE_SHIPMENT_QUERY,
  MARK_SHIPMENT_SHIPPED_MUTATION,
  SET_SCHEDULED_PICKUP_MUTATION,
  SHIPPABLE_CONTAINERS_QUERY,
  TENANT_WASTE_VENDOR_OPTIONS_QUERY,
  UPDATE_SHIPMENT_LINES_MUTATION,
  UPDATE_WASTE_SHIPMENT_MUTATION,
  VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT,
  VALIDATE_UPDATE_SHIPMENT_LINES_INPUT,
  VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT,
} from "./api";
import { ShipmentContentsCard } from "./ShipmentContentsCard";
import { formatShipmentDate, formatTotalsByUnit } from "./shipmentDisplay";
import { DisplayLine, useStagedShipmentLines } from "./useStagedShipmentLines";
import { WasteShipmentStatusChip } from "./WasteShipmentStatusChip";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;
type VendorOption = { id: string; name: string; usedByTenant: boolean };

// Shipping changes container amounts and statuses, and drops the shipped
// volume out of the accumulation area's rollup, so those reads have to refresh
// alongside the shipment ones — the ship path is the only thing that recomputes
// area rollups today.
const REFETCH = [
  "GetWasteShipment",
  "FacilityWasteShipments",
  "TenantWasteShipments",
  "GetWasteContainer",
  "FacilityWasteContainers",
  "TenantWasteContainers",
  "ShippableContainers",
  "FacilityAccumulationAreas",
  "FacilityAccumulationAreasTile",
  "GetAccumulationArea",
];

function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function FieldValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children ?? NO_VALUE_PLACEHOLDER}</Typography>
    </Grid>
  );
}

const isEditable = (status: WasteShipmentStatus) =>
  status === WasteShipmentStatus.Draft || status === WasteShipmentStatus.Requested;

export function WasteShipmentDetail() {
  const { tenantId } = useTenant();
  const { shipmentId } = useParams<{ shipmentId: string }>();

  const { data, loading } = useQuery(GET_WASTE_SHIPMENT_QUERY, {
    variables: { id: shipmentId ?? "" },
    skip: !shipmentId,
  });
  const shipment = data?.wasteShipment ?? null;

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments", to: tenantId ? `/o/${tenantId}/waste/shipments` : undefined },
    { label: shipment?.manifestRequestNumber ?? "Shipment" },
  ]);

  if (loading && !shipment) {
    return (
      <Stack spacing={3} sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3 }}>
          <Skeleton variant="text" width={130} height={36} />
          <Skeleton variant="rounded" width={72} height={24} />
        </Stack>
        <Paper elevation={0} sx={{ p: 3 }}>
          <Skeleton variant="text" width={90} height={28} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {Array.from({ length: 9 }).map((_, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                <Skeleton variant="text" width={90} />
                <Skeleton variant="text" width={140} />
              </Grid>
            ))}
          </Grid>
        </Paper>
        <Grid container spacing={2}>
          {[0, 1].map((i) => (
            <Grid key={i} size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 3 }}>
                <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={140} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>
    );
  }

  if (!shipment) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">Shipment not found</Typography>
      </Box>
    );
  }

  // Keyed on the shipment so a different one starts with clean staged state.
  return <LoadedShipmentDetail key={shipment.id} shipment={shipment} />;
}

/**
 * The shipment page edits as one unit: the header fields and the container
 * changes share a single Save and Discard. Removing a container and thinking
 * better of it should cost nothing, and a shipment should never be
 * half-edited — particularly once its request is in a vendor's hands.
 */
function LoadedShipmentDetail({ shipment }: { shipment: Shipment }) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const alerts = useAlerts();

  const [addOpen, setAddOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [editLine, setEditLine] = useState<DisplayLine | null>(null);

  const staged = useStagedShipmentLines(shipment);
  const form = useShipmentHeaderForm(shipment);
  const {
    formState: { isDirty: headerDirty, dirtyFields },
    getValues,
    reset,
    issues: headerIssues,
  } = form;

  const [update, { loading: updating }] = useMutation(UPDATE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [updateLines, { loading: savingLines }] = useMutation(UPDATE_SHIPMENT_LINES_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [setPickup, { loading: settingPickup }] = useMutation(SET_SCHEDULED_PICKUP_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [cancelShipment, { loading: canceling }] = useMutation(CANCEL_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });
  const [deleteShipment, { loading: deleting }] = useMutation(DELETE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: REFETCH,
  });

  const saving = updating || savingLines || settingPickup;
  const editable = isEditable(shipment.status);
  const unsavedCount = Object.keys(dirtyFields).length + staged.changeCount;
  const hasUnsaved = unsavedCount > 0;

  const handleSave = async () => {
    if (hasCriticalIssues(headerIssues)) {
      return;
    }
    const values = getValues();
    try {
      if (headerDirty) {
        await update({
          variables: {
            input: toUpdateShipmentInput(shipment.id, values, Boolean(dirtyFields.plannedShipDate)),
          },
        });
      }
      // One call for every container change — the mutation takes adds,
      // removals, and quantities together, so the batch lands atomically.
      const linesInput = staged.toUpdateLinesInput();
      if (linesInput) {
        await updateLines({ variables: { input: linesInput } });
      }
      // The pickup date is the one header field with an effect beyond the
      // shipment row: it flags the containers. Clearing it is a real edit —
      // it releases them — so the mutation runs whenever the field is dirty,
      // with null carrying the clear.
      if (dirtyFields.scheduledPickupDate) {
        await setPickup({
          variables: {
            shipmentId: shipment.id,
            scheduledPickupDate: values.scheduledPickupDate
              ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.scheduledPickupDate))
              : null,
          },
        });
      }
      staged.reset();
      // The header form keeps dirty values across a reset so the refetch can't
      // clobber someone mid-edit — which also means a successful save leaves it
      // looking unsaved. Rebase it on what was just persisted, or the page goes
      // on claiming changes and "Mark shipped" stays disabled until the user
      // discards an edit that already landed.
      reset(values);
      alerts.success("Shipment updated.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to update the shipment.");
    }
  };

  const handleDiscard = () => {
    reset();
    staged.reset();
  };

  // A draft never left the building, so it is deleted rather than left behind
  // as a canceled row; once requested, the vendor has its number and the
  // record stays.
  const isDraft = shipment.status === WasteShipmentStatus.Draft;

  const handleAbandon = async () => {
    try {
      if (isDraft) {
        await deleteShipment({ variables: { id: shipment.id } });
        alerts.success(`Shipment ${shipment.manifestRequestNumber} deleted.`);
        setCancelOpen(false);
        navigate(`/o/${tenantId}/waste/shipments`);
        return;
      }
      await cancelShipment({ variables: { id: shipment.id } });
      alerts.success(`Shipment ${shipment.manifestRequestNumber} canceled.`);
      setCancelOpen(false);
    } catch (e) {
      alerts.error(
        e instanceof Error ? e.message : `Failed to ${isDraft ? "delete" : "cancel"} the shipment.`
      );
    }
  };

  return (
    <Stack spacing={3} sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3 }}>
        <Typography variant="h5">{shipment.manifestRequestNumber}</Typography>
        <WasteShipmentStatusChip status={shipment.status} />
        {shipment.backfilledFromManifest && (
          <Chip label="Backfilled from manifest" size="small" variant="outlined" />
        )}
        <Box sx={{ flexGrow: 1 }} />
        {editable && (
          <>
            {hasUnsaved && (
              <Chip
                label={`${unsavedCount} unsaved ${unsavedCount === 1 ? "change" : "changes"}`}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
            <IssueListButton issues={headerIssues} />
            <Button size="small" onClick={handleDiscard} disabled={!hasUnsaved || saving}>
              Discard
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSave}
              disabled={!hasUnsaved || saving || hasCriticalIssues(headerIssues)}
            >
              Save
            </Button>
            <Button variant="outlined" size="small" onClick={() => setCancelOpen(true)}>
              {isDraft ? "Delete shipment" : "Cancel shipment"}
            </Button>
            {/* Shipping acts on what is saved, so unsaved edits have to land
                first or the removals would not match what is on screen. */}
            <Tooltip title={hasUnsaved ? "Save your changes before shipping" : ""}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LocalShipping />}
                  onClick={() => setShipOpen(true)}
                  disabled={hasUnsaved}
                >
                  Mark shipped
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Stack>

      {editable ? (
        <ShipmentDetailsFields shipment={shipment} form={form} />
      ) : (
        <SectionCard title="Details">
          <Grid container spacing={2}>
            <FieldValue label="Facility">{shipment.facility?.name}</FieldValue>
            <FieldValue label="Vendor">{shipment.vendor?.name}</FieldValue>
            <FieldValue label="Transporter">{shipment.transporterName}</FieldValue>
            <FieldValue label="Destination (TSDF)">{shipment.destinationName}</FieldValue>
            <FieldValue label="Destination EPA ID">{shipment.destinationEpaId}</FieldValue>
            <FieldValue label="Planned ship date">
              {formatShipmentDate(shipment.plannedShipDate)}
            </FieldValue>
            <FieldValue label="Scheduled pickup">
              {formatShipmentDate(shipment.scheduledPickupDate)}
            </FieldValue>
            <FieldValue label="Shipped">{formatShipmentDate(shipment.shippedDate)}</FieldValue>
            <FieldValue label="Total">
              {formatTotalsByUnit(shipment.totalQuantityByUnit)}
            </FieldValue>
          </Grid>
        </SectionCard>
      )}

      <ShipmentContentsCard
        shipment={shipment}
        lines={staged.displayLines}
        editable={editable}
        issues={shipment.issues}
        onAddContainers={() => setAddOpen(true)}
        onEditLine={setEditLine}
        onRemoveLine={staged.removeLine}
        onRestoreLine={staged.restoreLine}
      />

      {addOpen && (
        <AddContainersDialog
          shipment={shipment}
          stagedContainerIds={staged.stagedContainerIds}
          onStage={staged.addContainers}
          onClose={() => setAddOpen(false)}
        />
      )}
      {shipOpen && (
        <MarkShipmentShippedDialog shipment={shipment} onClose={() => setShipOpen(false)} />
      )}
      {editLine?.lineId && (
        <EditLineQuantityDialog
          shipmentId={shipment.id}
          line={{ ...editLine, lineId: editLine.lineId }}
          onStage={staged.setQuantity}
          onClose={() => setEditLine(null)}
        />
      )}
      {cancelOpen && (
        <Dialog
          open
          onClose={() => setCancelOpen(false)}
          maxWidth="xs"
          title={isDraft ? "Delete this shipment?" : "Cancel this shipment?"}
          primaryActionButtonProps={{
            saveText: isDraft ? "Delete shipment" : "Cancel shipment",
            loading: canceling || deleting,
            onClick: handleAbandon,
          }}
        >
          <Typography variant="body2">
            {isDraft
              ? `${shipment.manifestRequestNumber} has not gone to a vendor, so it will be deleted outright. Its containers are untouched and stay available to package.`
              : `${shipment.manifestRequestNumber} will be marked canceled and kept, because the vendor already has its request. No container amounts change — removals only happen when a shipment ships.`}
          </Typography>
        </Dialog>
      )}
    </Stack>
  );
}

type EditShipmentFormValues = {
  vendorId: string | null;
  transporterName: string;
  destinationName: string;
  destinationEpaId: string;
  plannedShipDate: string | null;
  scheduledPickupDate: string | null;
};

function toUpdateShipmentInput(
  shipmentId: string,
  values: EditShipmentFormValues,
  plannedShipDateDirty: boolean
) {
  return {
    id: shipmentId,
    vendorId: values.vendorId,
    transporterName: values.transporterName.trim() || null,
    destinationName: values.destinationName.trim() || null,
    destinationEpaId: values.destinationEpaId.trim() || null,
    // Only re-normalize the date when the user actually touched it, so an
    // untouched value can't shift a day through the local timezone.
    ...(plannedShipDateDirty
      ? {
          plannedShipDate: values.plannedShipDate
            ? toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.plannedShipDate))
            : null,
        }
      : {}),
  };
}

/**
 * Async header-edit validation; the seam future field checks land in. The
 * validation payload always includes the date (nothing persists from it, so
 * the dirty-gated timezone guard only matters on the real mutation input).
 */
function useShipmentUpdateValidation(shipmentId: string) {
  const [query] = useLazyQuery(VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT, {
    fetchPolicy: "no-cache",
  });
  return useCallback(
    async (values: EditShipmentFormValues): Promise<Issue[]> => {
      const { data } = await query({
        variables: { input: toUpdateShipmentInput(shipmentId, values, true) },
      });
      return data?.wasteShipmentUpdateInputValidator ?? [];
    },
    [query, shipmentId]
  );
}

/**
 * The shipment's header as a live inline form — the single place vendor,
 * transporter, TSDF, and dates are collected. Save/Discard appear only when
 * dirty; background refetches keep in-progress edits (keepDirtyValuesOnReset).
 * 3C note: once requests generate, a header edit after requestGeneratedAt
 * should surface a stale-request warning here.
 */
/** The header form, owned by the page so one Save can cover the whole thing. */
function useShipmentHeaderForm(shipment: Shipment) {
  const defaultValues = useMemo<EditShipmentFormValues>(
    () => ({
      vendorId: shipment.vendor?.id ?? null,
      transporterName: shipment.transporterName ?? "",
      destinationName: shipment.destinationName ?? "",
      destinationEpaId: shipment.destinationEpaId ?? "",
      plannedShipDate: storedDateToDateOnly(shipment.plannedShipDate),
      scheduledPickupDate: storedDateToDateOnly(shipment.scheduledPickupDate),
    }),
    [shipment]
  );

  return useValidatingForm<EditShipmentFormValues>(
    defaultValues,
    [],
    useShipmentUpdateValidation(shipment.id),
    [],
    { keepDirtyValuesOnReset: true }
  );
}

function ShipmentDetailsFields({
  shipment,
  form,
}: {
  shipment: Shipment;
  form: ReturnType<typeof useShipmentHeaderForm>;
}) {
  const { tenantId } = useTenant();
  const { data: vendorData, loading: vendorsLoading } = useQuery(
    TENANT_WASTE_VENDOR_OPTIONS_QUERY,
    { variables: { tenantId: tenantId ?? "" }, skip: !tenantId }
  );
  const vendorOptions: VendorOption[] = vendorData?.tenant?.wasteVendorOptions ?? [];
  const { control } = form;

  return (
    <SectionCard title="Details">
      <FormProvider {...form}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FieldValue label="Facility">{shipment.facility?.name}</FieldValue>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Controller
              control={control}
              name="vendorId"
              render={({ field }) => (
                <Autocomplete
                  options={vendorOptions}
                  getOptionLabel={(o: VendorOption) => o.name}
                  groupBy={(o: VendorOption) => (o.usedByTenant ? "Your vendors" : "All vendors")}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={vendorOptions.find((o) => o.id === field.value) ?? null}
                  onChange={(_e, value) => field.onChange(value?.id ?? null)}
                  loading={vendorsLoading}
                  fullWidth
                  renderInput={(params) => <TextField {...params} label="Vendor" size="small" />}
                  noOptionsText="No vendors found in this organization's waste data"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormTextField
              control={control}
              name="transporterName"
              label="Transporter"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormTextField
              control={control}
              name="destinationName"
              label="Destination (TSDF)"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormTextField
              control={control}
              name="destinationEpaId"
              label="Destination EPA ID"
              fullWidth
              textFieldProps={{ size: "small" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DateField
              control={control}
              name="plannedShipDate"
              label="Planned ship date"
              datePickerProps={{ slotProps: { textField: { size: "small", fullWidth: true } } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DateField
              control={control}
              name="scheduledPickupDate"
              label="Scheduled pickup"
              helperText="Flags this shipment's containers as awaiting pickup"
              datePickerProps={{ slotProps: { textField: { size: "small", fullWidth: true } } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FieldValue label="Shipped">{formatShipmentDate(shipment.shippedDate)}</FieldValue>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FieldValue label="Total">
              {formatTotalsByUnit(shipment.totalQuantityByUnit)}
            </FieldValue>
          </Grid>
        </Grid>
      </FormProvider>
    </SectionCard>
  );
}

function AddContainersDialog({
  shipment,
  stagedContainerIds,
  onStage,
  onClose,
}: {
  shipment: Shipment;
  /** Containers already on the shipment or already staged. */
  stagedContainerIds: string[];
  onStage: (containers: ShippableContainerItemFragment[]) => void;
  onClose: () => void;
}) {
  const alerts = useAlerts();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data, loading } = useQuery(SHIPPABLE_CONTAINERS_QUERY, {
    variables: { facilityId: shipment.facilityId },
    fetchPolicy: "cache-and-network",
  });
  const onShipment = useMemo(() => new Set(stagedContainerIds), [stagedContainerIds]);
  const candidates = useMemo(
    () => (data?.facility?.shippableContainers?.items ?? []).filter((c) => !onShipment.has(c.id)),
    [data?.facility?.shippableContainers?.items, onShipment]
  );

  // Live warn-level validation as the selection changes — e.g. a container
  // already on another open shipment warns here just like in the builder.
  const [validate, { data: validationData }] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  useEffect(() => {
    if (selectedIds.size > 0) {
      validate({
        variables: { input: { shipmentId: shipment.id, addContainerIds: [...selectedIds] } },
      });
    }
  }, [selectedIds, shipment.id, validate]);
  const issues: Issue[] =
    selectedIds.size > 0 ? (validationData?.updateShipmentLinesInputValidator ?? []) : [];

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  // Staged, not saved: the page has one Save, and adding a container should be
  // as undoable as any other edit on it.
  const handleAdd = () => {
    onStage(candidates.filter((c) => selectedIds.has(c.id)));
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title="Add containers"
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Add",
        disabled: selectedIds.size === 0 || hasCriticalIssues(issues),
        onClick: handleAdd,
      }}
    >
      {loading && candidates.length === 0 && <Skeleton height={120} />}
      {!loading && candidates.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Every shippable container at this facility is already on the shipment.
        </Typography>
      )}
      <Stack sx={{ maxHeight: 360, overflowY: "auto" }}>
        {candidates.map((container) => (
          <Stack key={container.id} direction="row" alignItems="center" spacing={1.5}>
            <Checkbox
              size="small"
              checked={selectedIds.has(container.id)}
              onChange={() => toggle(container.id)}
              inputProps={{ "aria-label": `Select ${container.containerLabel}` }}
            />
            <Typography variant="subtitle2" sx={{ minWidth: 90 }}>
              {container.containerLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              {container.wasteStream?.name ?? "Uncharacterized"}
            </Typography>
            <Typography variant="body2">
              {container.amount != null && container.amountUnit
                ? formatQuantity(container.amount, container.amountUnit)
                : NO_VALUE_PLACEHOLDER}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {issues.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {issues.map((issue, i) => (
            <Alert
              key={`${issue.key}-${i}`}
              severity={issue.level === "CRITICAL" ? "error" : "warning"}
            >
              {issue.message}
            </Alert>
          ))}
        </Stack>
      )}
    </Dialog>
  );
}

/**
 * The validator's input types the quantity as a required Float, so a value that
 * isn't a number can't be sent at all — it serializes to null and the query
 * errors, which would otherwise surface as "no issues" and leave Save enabled.
 */
const UNPARSEABLE_QUANTITY_ISSUE: Issue = {
  level: IssueLevel.Critical,
  key: "lineQuantityInvalid",
  message: "Enter the quantity as a number.",
  modelName: "WasteShipmentLine",
  dataName: null,
  jurisdictions: [],
};

type EditLineQuantityFormValues = {
  quantity: string;
  unit: UnitType;
};

function EditLineQuantityDialog({
  shipmentId,
  line,
  onStage,
  onClose,
}: {
  shipmentId: string;
  line: DisplayLine & { lineId: string };
  onStage: (lineId: string, quantity: number, unit: UnitType) => void;
  onClose: () => void;
}) {
  const alerts = useAlerts();

  const defaultValues = useMemo<EditLineQuantityFormValues>(
    () => ({ quantity: String(line.quantity), unit: line.unit }),
    [line]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: EditLineQuantityFormValues): Promise<Issue[]> => {
      const quantity = safeParseFloat(values.quantity);
      if (quantity === null) {
        return [UNPARSEABLE_QUANTITY_ISSUE];
      }
      const { data } = await validateQuery({
        variables: {
          input: {
            shipmentId,
            setQuantities: [{ lineId: line.lineId, quantity, unit: values.unit }],
          },
        },
      });
      return data?.updateShipmentLinesInputValidator ?? [];
    },
    [validateQuery, shipmentId, line.lineId]
  );

  const form = useValidatingForm<EditLineQuantityFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    watch,
    formState: { isDirty },
    issues,
  } = form;
  const quantityValue = watch("quantity");

  const handleSave = () => {
    if (hasCriticalIssues(issues)) {
      return;
    }
    const values = getValues();
    const quantity = safeParseFloat(values.quantity);
    if (quantity === null) {
      return;
    }
    onStage(line.lineId, quantity, values.unit);
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={`Quantity for ${line.container.containerLabel}`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Apply",
        disabled: hasCriticalIssues(issues) || !isDirty || safeParseFloat(quantityValue) === null,
        onClick: handleSave,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Ship less than the full container (partial ship) or set the amount leaving a tank.
          </Typography>
          <FormTextField
            control={control}
            name="quantity"
            label="Quantity"
            fullWidth
            type="number"
            inputProps={{ min: 0, step: "any" }}
            helperText={`Container holds ${formatQuantity(line.container.amount, line.container.amountUnit)}`}
            textFieldProps={{ size: "small" }}
          />
          <Controller
            control={control}
            name="unit"
            render={({ field }) => (
              <TextField
                label="Unit"
                size="small"
                select
                value={field.value}
                onChange={(e) => field.onChange(e.target.value as UnitType)}
              >
                {Object.values(UnitType).map((u) => (
                  <MenuItem key={u} value={u}>
                    {UNIT_LABELS[u] ?? u}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  );
}

/**
 * The ship dialog's starting date: the planned ship date when there is one and
 * it has arrived, otherwise today. A planned date still in the future would be
 * rejected as a future shipped date, so it is not offered.
 */
function plannedShippedDateDefault(plannedShipDate: string | null | undefined): string | null {
  const today = DateTime.now().startOf("day");
  const plannedDay = storedDateToDateOnly(plannedShipDate);
  if (plannedDay) {
    const planned = DateTime.fromISO(plannedDay).startOf("day");
    if (planned.isValid && planned <= today) {
      return planned.toISODate();
    }
  }
  return today.toISODate();
}

/**
 * A stored shipment date is UTC midnight standing for a calendar day, but the
 * date picker parses what it is given in the browser's zone — so handing it the
 * raw instant renders the day before for anyone behind UTC. Read the day out in
 * UTC and pass a plain date, which the picker shows verbatim.
 *
 * Done here rather than in DateField: that component is shared by 26 call
 * sites, some of which mean a real instant.
 */
function storedDateToDateOnly(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  return DateTime.fromISO(iso, { zone: "utc" }).toISODate();
}

type ShipFormValues = { shippedDate: string | null };

/**
 * Mirrors the server rule's key and message: the input serializes the picked
 * date to UTC midnight, so the server can no longer tell which calendar day the
 * user was on and keeps a looser end-of-day tolerance. Deciding it here — as the
 * container fill dialogs do — is what makes "tomorrow" wrong for a user whose
 * today is already the server's tomorrow.
 */
const SHIPPED_DATE_IN_FUTURE_ISSUE: Issue = {
  level: IssueLevel.Critical,
  key: "shippedDateInFuture",
  message: "The shipped date cannot be in the future.",
  modelName: "WasteShipment",
  dataName: null,
  jurisdictions: [],
};

function toShipInput(shipmentId: string, values: ShipFormValues) {
  return {
    shipmentId,
    shippedDate: values.shippedDate
      ? (toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.shippedDate)) ?? null)
      : null,
  };
}

/**
 * The commit step, not a second editor. What is on the shipment and how much
 * of each container leaves are decisions made on the shipment page, so this
 * confirms the date and shows the one thing the page cannot: what shipping
 * will actually do to each container, and whether anything blocks it.
 */
function MarkShipmentShippedDialog({
  shipment,
  onClose,
}: {
  shipment: Shipment;
  onClose: () => void;
}) {
  const alerts = useAlerts();

  const defaultValues = useMemo<ShipFormValues>(
    () => ({
      // The planned date is what the user already told us this shipment was
      // going out on, so it beats a guess. Falls back to today when the
      // shipment was never planned, or when the plan is still in the future
      // and the waste is leaving now.
      shippedDate: plannedShippedDateDefault(shipment.plannedShipDate),
    }),
    [shipment.plannedShipDate]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_MARK_SHIPMENT_SHIPPED_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: ShipFormValues): Promise<Issue[]> => {
      const input = toShipInput(shipment.id, values);
      if (!input.shippedDate) {
        return [];
      }
      if (isLocalDateInFuture(values.shippedDate)) {
        return [SHIPPED_DATE_IN_FUTURE_ISSUE];
      }
      const { data } = await validateQuery({ variables: { input } });
      return data?.markShipmentShippedInputValidator ?? [];
    },
    [validateQuery, shipment.id]
  );

  // Seeded from the shipment's own standing issues so a blocked shipment says
  // so the moment this opens, without waiting on a round-trip.
  const form = useValidatingForm<ShipFormValues>(defaultValues, shipment.issues, validateInput, []);
  const { control, getValues, watch, issues } = form;

  const [markShipped, { loading }] = useMutation(MARK_SHIPMENT_SHIPPED_MUTATION, {
    refetchQueries: REFETCH,
  });

  const shippedDate = watch("shippedDate");
  const blocked = hasCriticalIssues(issues) || !shippedDate || loading;
  const blockers = issues.filter((issue) => issue.level === IssueLevel.Critical);
  const preview = shipment.shipPreview;

  const effects = [
    preview.archivedCount > 0
      ? `archive ${preview.archivedCount} one-way ${preview.archivedCount === 1 ? "container" : "containers"}`
      : null,
    preview.emptiedTankCount > 0
      ? `empty ${preview.emptiedTankCount} ${preview.emptiedTankCount === 1 ? "tank" : "tanks"} and return ${preview.emptiedTankCount === 1 ? "it" : "them"} to service`
      : null,
  ].filter((effect): effect is string => effect !== null);

  const handleShip = async () => {
    if (blocked) {
      return;
    }
    const input = toShipInput(shipment.id, getValues());
    if (!input.shippedDate) {
      return;
    }
    try {
      await markShipped({ variables: { input } });
      alerts.success(`${shipment.manifestRequestNumber} is marked shipped.`);
      onClose();
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to mark the shipment shipped.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      title={`Mark ${shipment.manifestRequestNumber} shipped`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Mark shipped",
        loading,
        disabled: blocked,
        onClick: handleShip,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This records {formatTotalsByUnit(shipment.totalQuantityByUnit)} across{" "}
            {shipment.lines.length} {shipment.lines.length === 1 ? "container" : "containers"} as
            having left the site. To change what is on the shipment, or how much of a container
            leaves, close this and edit its contents.
          </Typography>

          <Box>
            <DateField
              control={control}
              name="shippedDate"
              label="Shipped date"
              datePickerProps={{
                disableFuture: true,
                slotProps: { textField: { size: "small", sx: { width: 220 } } },
              }}
            />
          </Box>

          {blockers.length > 0 ? (
            <Alert severity="error">
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {blockers.length} {blockers.length === 1 ? "problem" : "problems"} to fix before
                this can ship
              </Typography>
              <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.25}>
                {blockers.map((issue, i) => (
                  <Typography key={`${issue.key}-${i}`} component="li" variant="caption">
                    {issue.message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Shipping this will
              </Typography>
              <Stack component="ul" sx={{ pl: 2.5, m: 0 }} spacing={0.25}>
                {effects.map((effect) => (
                  <Typography key={effect} component="li" variant="body2" color="text.secondary">
                    {effect}
                  </Typography>
                ))}
                {preview.keepsBalance.map((balance) => (
                  <Typography
                    key={balance.containerLabel}
                    component="li"
                    variant="body2"
                    color="text.secondary"
                  >
                    leave {balance.containerLabel} on site with{" "}
                    {formatQuantity(balance.remaining, balance.unit)}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {issues
            .filter((issue) => issue.level !== IssueLevel.Critical)
            .map((issue, i) => (
              <Alert key={`${issue.key}-${i}`} severity="warning">
                {issue.message}
              </Alert>
            ))}

          {!shipment.manifestReceivedAt && (
            <Alert severity="info">
              No manifest is attached yet. That&apos;s fine — the hauler usually leaves one at
              pickup, and you can attach it later.
            </Alert>
          )}
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
